import { useMemo, useState } from 'react';
import type { Label } from '../../data/store';
import { storeLayout } from '../../router';
import { StatusCell } from '../status/status-cell';

export function CollapsingProductLabels({ labelIds }: { labelIds: number[] }) {
  const { store } = storeLayout.useRouteContext();

  const labels = useMemo(() => {
    return store.labels
      .filter((label) => labelIds.includes(label.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [labelIds, store.labels]);

  return (
    <div className="flex gap-1">
      {labels.map((label) => (
        <LabelDisplay key={label.id} label={label} />
      ))}
    </div>
  );
}

const LabelDisplay = ({ label }: { label: Label }) => {
  const [isInteracted, setIsInteracted] = useState(false);

  const labelString = useMemo(() => {
    if (isInteracted) {
      return label.label;
    }

    return label.label
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }, [isInteracted, label.label]);

  return (
    <StatusCell
      color={label.color}
      // Tab into a collapsed label to see the full name. The expansion from
      // initials to full label IS the focus indicator — no outline needed
      // (the global :focus-visible outline rendered with artifacts on the
      // small badges).
      tabIndex={0}
      aria-label={label.label}
      onMouseEnter={() => setIsInteracted(true)}
      onMouseLeave={() => setIsInteracted(false)}
      onFocus={() => setIsInteracted(true)}
      onBlur={() => setIsInteracted(false)}
      // cursor:help hints the initials pill expands to the full label name on
      // hover or focus — the pill gives no other first-sight cue (CF-4).
      className="cursor-help focus:outline-none focus-visible:outline-none"
    >
      {labelString}
    </StatusCell>
  );
};
